import { useCallback } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { useCustomerActions, useNotificationActions, usePerformanceActions } from 'src/store';
import { useCipEvents, useConfirmUnauthorizeFromChannel } from 'src/services';
import { usePopups } from 'src/services/popup';
import { AD_ACCOUNTS_SELECTION_POPUP } from '../types';
import { formatProjectRoute } from 'src/lib/routing';
import { ROUTE_PARAMS } from 'src/routes';
import { NOTIFICATION_LEVEL } from 'src/data/notifications';

export const useAdAccountsSelectionPopup = () => {
  const popups = usePopups();
  const performanceActions = usePerformanceActions();
  const customerActions = useCustomerActions();
  const cipEvents = useCipEvents();
  const confirmUnauthorizeFromChannel = useConfirmUnauthorizeFromChannel();
  const notifications = useNotificationActions();

  return useCallback(
    ({ channel, projectId, variationId }) => {
      return new Promise((resolve) => {
        popups.openPopup(AD_ACCOUNTS_SELECTION_POPUP, {
          channel,
          onClose: async () => {
            resolve();
            popups.closePopup(AD_ACCOUNTS_SELECTION_POPUP);
            const selectedAccountIds = performanceActions.getChannelSelectedAccountIds(channel);
            if (projectId && variationId && selectedAccountIds?.length) {
              notifications.displayMessage(
                <>
                  <span>Processing your data.</span>{' '}
                  <Link
                    to={formatProjectRoute(projectId, {
                      [ROUTE_PARAMS.variationId]: variationId,
                    })}
                  >
                    Return to document
                  </Link>
                </>,
                null,
                {
                  timeout: 5000,
                  level: NOTIFICATION_LEVEL.other,
                  hideIcon: true,
                  hideCloseButton: true,
                }
              );
            }
          },
          onDisconnect: async () => {
            const confirmed = await confirmUnauthorizeFromChannel(channel);
            if (!confirmed) {
              return;
            }
            cipEvents.trackDisconnectChannel(channel);

            performanceActions.disconnectFromChannel(channel);
            performanceActions.cancelWaitRequests(channel);
            performanceActions
              .updateConnectedAccounts({ channel, selectedAccountIds: [] })
              .then(() => {
                // Refresh customer limits and rows allocation, cause campaign status has been changed, do not wait for result
                customerActions.refreshCurrentCustomerFeatureLimit();
                customerActions.refreshCurrentCustomerRowsAllocation();
                performanceActions.refreshConnectedAccounts();
                popups.closePopup(AD_ACCOUNTS_SELECTION_POPUP);
              });
          },
          onSubmit: ({ channel, accountId, action, selectedAccountIds }) => {
            cipEvents.trackConnectAdAccounts(channel, accountId, action);

            if (selectedAccountIds.length) {
              performanceActions
                .extractTextVariations({
                  channel,
                  selectedAccountIds,
                })
                .then(() => {
                  // Refresh customer limits and rows allocation, cause campaign status has been changed, do not wait for result
                  customerActions.refreshCurrentCustomerFeatureLimit();
                  customerActions.refreshCurrentCustomerRowsAllocation();
                  performanceActions.refreshConnectedAccounts();
                });
            } else {
              performanceActions.cancelWaitRequests(channel);
              performanceActions
                .updateConnectedAccounts({ channel, selectedAccountIds })
                .then(() => {
                  // Refresh customer limits and rows allocation, cause campaign status has been changed, do not wait for result
                  customerActions.refreshCurrentCustomerFeatureLimit();
                  customerActions.refreshCurrentCustomerRowsAllocation();
                  performanceActions.refreshConnectedAccounts();
                });
            }

            resolve({ channel, accountId, action, selectedAccountIds });
          },
        });
      });
    },
    [
      performanceActions,
      cipEvents,
      popups,
      confirmUnauthorizeFromChannel,
      customerActions,
      notifications,
    ]
  );
};
